import { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { User, Storefront, UsersThree, ArrowRight, ArrowLeft, CheckCircle, MagnifyingGlass, Warning } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api, { categoryAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Post-registration onboarding wizard.
 *
 * Step 1: choose role (cliente / proveedor / ambos).
 * Step 2 (only for proveedor/ambos): pick specialty categories.
 * Step 3 (only for proveedor/ambos): reminder about Constancia de Situación Fiscal.
 *
 * Shown automatically by `OnboardingGate` whenever
 * `user.onboarding_completed === false`.
 */
export const OnboardingWizard = () => {
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [role, setRole] = useState(user?.role || 'cliente');
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(user?.specialties || []);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    categoryAPI.getAll().then((res) => setCategories(res.data || [])).catch(() => setCategories([]));
  }, []);

  const needsSpecialties = role === 'proveedor' || role === 'ambos';

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    const q = search.toLowerCase();
    return categories.filter((c) => (c.name || '').toLowerCase().includes(q));
  }, [categories, search]);

  const toggleCategory = (name) => {
    setSelected((prev) => prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]);
  };

  const canContinueFromStep1 = !!role;
  const canContinueFromStep2 = !needsSpecialties || selected.length > 0;

  const goNext = () => {
    if (step === 1) {
      if (!canContinueFromStep1) return;
      setStep(needsSpecialties ? 2 : 99); // skip step 2 for client
    } else if (step === 2) {
      if (!canContinueFromStep2) {
        toast.error('Selecciona al menos una categoría para continuar.');
        return;
      }
      setStep(3);
    }
  };

  const goBack = () => {
    if (step === 99) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 2) setStep(1);
  };

  const finish = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post('/users/complete-onboarding', {
        role,
        specialties: needsSpecialties ? selected : [],
      });
      updateUser(res.data);
      toast.success('¡Listo! Tu perfil básico quedó configurado.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo completar la configuración.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  const isClientStep = step === 99;
  const isReminderStep = step === 3;

  return (
    <div
      className="fixed inset-0 z-[9998] bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 shadow-2xl my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5">
          <p className="text-xs text-orange-100 uppercase tracking-widest mb-1">
            Bienvenido{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </p>
          <h2 className="text-xl font-bold text-white">Configura tu perfil en MaterMatch</h2>
          <p className="text-sm text-orange-50 mt-1">
            {step === 1 && 'Paso 1 de ' + (needsSpecialties ? '3' : '2') + ': ¿Cómo vas a usar la plataforma?'}
            {step === 2 && 'Paso 2 de 3: ¿Qué categorías de materiales vas a ofrecer?'}
            {step === 3 && 'Paso 3 de 3: Información fiscal requerida'}
            {step === 99 && 'Último paso: confirmación'}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          {step === 1 && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Elige el rol que mejor describe cómo usarás MaterMatch. Podrás cambiarlo después desde tu perfil.
              </p>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { value: 'cliente', title: 'Soy Cliente', desc: 'Quiero crear proyectos y recibir cotizaciones de proveedores.', icon: User },
                  { value: 'proveedor', title: 'Soy Proveedor', desc: 'Quiero ofrecer materiales y recibir solicitudes de cotización.', icon: Storefront },
                  { value: 'ambos', title: 'Soy Ambos', desc: 'A veces compro para mis obras y a veces vendo materiales.', icon: UsersThree },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const active = role === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRole(opt.value)}
                      className={`text-left border-2 p-4 flex items-start gap-4 transition-all ${
                        active
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
                          : 'border-zinc-200 dark:border-zinc-700 hover:border-orange-300'
                      }`}
                      data-testid={`onboarding-role-${opt.value}`}
                    >
                      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${
                        active ? 'bg-orange-500 text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'
                      }`}>
                        <Icon className="w-5 h-5" weight={active ? 'fill' : 'bold'} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold ${active ? 'text-orange-700 dark:text-orange-300' : 'text-zinc-900 dark:text-zinc-100'}`}>
                          {opt.title}
                        </p>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 leading-snug">
                          {opt.desc}
                        </p>
                      </div>
                      {active && (
                        <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0" weight="fill" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Selecciona <span className="font-semibold">todas las categorías de materiales</span> que ofreces.
                Solo verás cotizaciones de estas categorías (podrás cambiarlas después en tu perfil).
              </p>
              <div className="relative">
                <MagnifyingGlass className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Buscar categoría..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-none h-10 pl-9"
                  data-testid="onboarding-category-search"
                />
              </div>
              <div className="border border-zinc-200 dark:border-zinc-700 max-h-80 overflow-y-auto p-3 space-y-1" data-testid="onboarding-category-list">
                {filteredCategories.length === 0 && (
                  <p className="text-sm text-zinc-400 italic p-4 text-center">Sin resultados.</p>
                )}
                {filteredCategories.map((cat) => {
                  const active = selected.includes(cat.name);
                  return (
                    <label
                      key={cat.id || cat.name}
                      className={`flex items-center gap-3 cursor-pointer px-3 py-2 transition-colors ${
                        active ? 'bg-orange-50 dark:bg-orange-950/30' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={active}
                        onChange={() => toggleCategory(cat.name)}
                        className="w-4 h-4 accent-orange-500 flex-shrink-0"
                        data-testid={`onboarding-cat-${cat.name}`}
                      />
                      <span className={`text-sm ${active ? 'font-semibold text-orange-700 dark:text-orange-300' : 'text-zinc-700 dark:text-zinc-300'}`}>
                        {cat.name}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Categorías seleccionadas: <span className="font-semibold text-orange-600">{selected.length}</span>
              </p>
            </>
          )}

          {step === 3 && (
            <>
              <div className="border-2 border-orange-400 bg-orange-50 dark:bg-orange-950/30 p-5 flex gap-4">
                <Warning className="w-8 h-8 text-orange-600 flex-shrink-0 mt-0.5" weight="fill" />
                <div className="space-y-3">
                  <p className="font-bold text-orange-900 dark:text-orange-200 uppercase tracking-wide text-sm">
                    Para recibir cotizaciones necesitarás tu Constancia de Situación Fiscal
                  </p>
                  <p className="text-sm text-orange-900 dark:text-orange-100 leading-relaxed">
                    Para acceder a las cotizaciones de tus categorías deberás subir tu <strong>Constancia de
                    Situación Fiscal (CSF)</strong> actualizada (no mayor a 3 meses). La plataforma validará que
                    los datos del PDF coincidan con tu información fiscal.
                  </p>
                  <ul className="text-sm text-orange-900 dark:text-orange-100 list-disc list-inside space-y-1">
                    <li>Obténla gratis en el portal del SAT.</li>
                    <li>Debe coincidir con tu RFC, razón social y régimen fiscal.</li>
                    <li>Debes renovarla cada 3 meses.</li>
                  </ul>
                  <a
                    href="https://www.sat.gob.mx/aplicacion/login/53027/emite-tu-constancia-de-situacion-fiscal"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block text-sm text-orange-700 dark:text-orange-300 font-semibold underline pt-1"
                  >
                    Descargar mi constancia en el SAT →
                  </a>
                </div>
              </div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                Puedes hacerlo ahora mismo desde tu perfil, o más tarde. Por el momento podrás navegar la
                plataforma; para ver cotizaciones disponibles necesitarás haberla subido.
              </p>
            </>
          )}

          {step === 99 && (
            <div className="border-2 border-green-500 bg-green-50 dark:bg-green-950/30 p-5 flex gap-4">
              <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0 mt-0.5" weight="fill" />
              <div>
                <p className="font-bold text-green-900 dark:text-green-200 uppercase tracking-wide text-sm">
                  Todo listo
                </p>
                <p className="text-sm text-green-900 dark:text-green-100 mt-2 leading-relaxed">
                  Te configuramos como <strong>Cliente</strong>. Desde tu dashboard podrás crear tu primer
                  proyecto y recibir cotizaciones de proveedores verificados.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-800 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            onClick={goBack}
            disabled={step === 1 || submitting}
            className="rounded-none"
            data-testid="onboarding-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>

          {(step === 1 || step === 2) && (
            <Button
              type="button"
              onClick={goNext}
              disabled={(step === 1 && !canContinueFromStep1) || (step === 2 && !canContinueFromStep2) || submitting}
              className="rounded-none btn-primary h-11 px-6 disabled:opacity-40"
              data-testid="onboarding-next-btn"
            >
              Continuar <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}

          {(step === 3 || step === 99) && (
            <Button
              type="button"
              onClick={finish}
              disabled={submitting}
              className="rounded-none btn-primary h-11 px-6 disabled:opacity-40"
              data-testid="onboarding-finish-btn"
            >
              {submitting ? 'Guardando…' : 'Finalizar y entrar'} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
